import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type ToastVariant = 'success' | 'error' | 'info';

type ToastItem = {
  id: number;
  message: string;
  variant: ToastVariant;
};

type ToastListProps = {
  items: ToastItem[];
  onDismiss: (id: number) => void;
};

export type UseToastOptions = {
  autoDismissMs?: number;
};

const CONFIG: Record<ToastVariant, { icon: React.ReactNode; bar: string; card: string; iconWrap: string }> = {
  success: {
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
    bar: 'bg-emerald-500',
    card: 'bg-gray-900 border-gray-700',
    iconWrap: 'bg-emerald-500/15 text-emerald-400',
  },
  error: {
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    bar: 'bg-red-500',
    card: 'bg-gray-900 border-gray-700',
    iconWrap: 'bg-red-500/15 text-red-400',
  },
  info: {
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01" />
      </svg>
    ),
    bar: 'bg-cyan-500',
    card: 'bg-gray-900 border-gray-700',
    iconWrap: 'bg-cyan-500/15 text-cyan-400',
  },
};

function ToastItem({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const cfg = CONFIG[item.variant];

  return (
    <div
      role="alert"
      className={`relative flex items-center gap-3 pl-4 pr-3 py-3.5 rounded-xl border shadow-2xl text-sm overflow-hidden min-w-[280px] max-w-sm backdrop-blur-sm ${cfg.card}`}
      style={{ animation: 'toast-in 0.28s cubic-bezier(0.34,1.56,0.64,1) both' }}
    >
      <span className={`absolute left-0 inset-y-0 w-1 rounded-l-xl ${cfg.bar}`} />
      <span className={`flex items-center justify-center h-8 w-8 rounded-lg shrink-0 ${cfg.iconWrap}`}>
        {cfg.icon}
      </span>
      <span className="flex-1 text-gray-100 font-medium leading-snug">{item.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss"
        className="shrink-0 p-1 rounded-md text-gray-500 hover:text-gray-200 hover:bg-gray-700/60 transition-colors"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/** Viewport top-right — shared by Prompt Gallery, Admin, and any other caller. */
export function ToastList({ items, onDismiss }: ToastListProps) {
  if (items.length === 0) return null;

  return createPortal(
    <>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(-12px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div className="fixed top-6 right-6 z-[10100] flex flex-col gap-2.5 items-end">
        {items.map(item => (
          <ToastItem key={item.id} item={item} onDismiss={onDismiss} />
        ))}
      </div>
    </>,
    document.body,
  );
}

let _nextId = 1;

/** @param autoDismissMsOrOptions number (legacy) or options object */
export function useToast(autoDismissMsOrOptions: number | UseToastOptions = 4000) {
  const opts: UseToastOptions =
    typeof autoDismissMsOrOptions === 'number'
      ? { autoDismissMs: autoDismissMsOrOptions }
      : autoDismissMsOrOptions;

  const autoDismissMs = opts.autoDismissMs ?? 4000;

  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setItems(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, variant: ToastVariant = 'success') => {
      const id = _nextId++;
      setItems(prev => [...prev, { id, message, variant }]);
      if (autoDismissMs > 0) {
        const timer = setTimeout(() => dismiss(id), autoDismissMs);
        timers.current.set(id, timer);
      }
    },
    [autoDismissMs, dismiss],
  );

  useEffect(() => {
    const t = timers.current;
    return () => {
      t.forEach(clearTimeout);
    };
  }, []);

  const toast = <ToastList items={items} onDismiss={dismiss} />;

  return { show, toast };
}
